/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch } from "react";
import { When } from "react-if";
import { Text } from "@nordcloud/gnui";
import {
  CloudAccountCloudProvider,
  ResourceCloudProvider,
  OsType,
  OsSubType,
} from "~/generated/graphql";
import { Mode, MultiTagsInput } from "~/components";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDateRange } from "~/components/SidebarWithMultiSelect/SelectableDateRange";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { Item } from "~/components/SidebarWithMultiSelect/types";
import {
  getDateRangeSelectionsText,
  getNestedSelectionsText,
} from "~/components/SidebarWithMultiSelect/utils";
import { isNotEmpty, isNotNil } from "~/tools";
import { useGetResourceCloudRegions } from "../../hooks/useGetResourceCloudRegions/useGetResourceCloudRegions";
import { State } from "./types";

type Props = {
  state: State;
  updateState: Dispatch<Partial<State>>;
  provider?: CloudAccountCloudProvider;
};

const cloudProviderCheckboxes: Item[] = [
  {
    value: ResourceCloudProvider.Aws,
    label: ResourceCloudProvider.Aws,
  },
  {
    value: ResourceCloudProvider.Azure,
    label: ResourceCloudProvider.Azure,
  },
  {
    value: ResourceCloudProvider.Gc,
    label: ResourceCloudProvider.Gc,
  },
  {
    value: ResourceCloudProvider.Ibm,
    label: ResourceCloudProvider.Ibm,
  },
  {
    value: ResourceCloudProvider.Other,
    label: ResourceCloudProvider.Other,
  },
];

const operatingSystemCheckboxes: Item[] = [
  {
    value: OsType.Windows,
    label: "Windows",
    subCheckboxes: [
      {
        value: OsSubType.WindowsServer,
        label: "Windows Server",
      },
    ],
  },
  {
    value: OsType.Linux,
    label: "Linux",
    subCheckboxes: [
      {
        value: OsSubType.Amazon,
        label: "Amazon",
      },
      {
        value: OsSubType.Centos,
        label: "CentOS",
      },
      {
        value: OsSubType.Debian,
        label: "Debian",
      },
      {
        value: OsSubType.OracleLinux,
        label: "Oracle Linux",
      },
      {
        value: OsSubType.Redhat,
        label: "Red Hat",
      },
      {
        value: OsSubType.Suse,
        label: "Suse",
      },
      {
        value: OsSubType.Ubuntu,
        label: "Ubuntu",
      },
    ],
  },
];
// eslint-disable-next-line complexity
export function ResourceDetailsFilters({
  state,
  updateState,
  provider,
}: Props) {
  const { data: resourceCloudRegions, loading: cloudProviderRegionsLoading } =
    useGetResourceCloudRegions(provider);

  const regionsCheckboxes = resourceCloudRegions?.map((region) => ({
    value: region,
    label: region,
  }));

  const dateAddedSelectionText = getDateRangeSelectionsText(
    state.dateAddedStart,
    state.dateAddedEnd
  );
  const operatingSystemSelectionText = getNestedSelectionsText({
    primaryCount: state.operatingSystems?.length ?? 0,
    primaryLabel: "OS",
    secondaryCount: state.operatingSystemSubTypes?.length ?? 0,
    secondaryLabel: "Version",
    modeLabel: state.excludeOperatingSystems ? "Exclude" : "Include",
  });
  const selectedTagsCount = state.resourceTagKeysAndTagValues?.length ?? 0;
  const tagsSelectionText =
    selectedTagsCount > 0
      ? `Include: ${selectedTagsCount} tag${selectedTagsCount === 1 ? "" : "s"}`
      : undefined;

  const clearDateAddedFilter = () => {
    updateState({ dateAddedStart: undefined, dateAddedEnd: undefined });
  };

  const clearOperatingSystemsFilter = () => {
    updateState({
      cloudProviders: undefined,
      excludeCloudProviders: undefined,
    });
  };

  const clearOperatingSystemFilter = () => {
    updateState({
      operatingSystems: undefined,
      operatingSystemSubTypes: undefined,
      excludeOperatingSystems: undefined,
    });
  };

  const clearRegionsFilter = () => {
    updateState({ regions: undefined, excludeRegions: undefined });
  };

  const clearResourceTagKeysAndTagValuesFilter = () => {
    updateState({
      resourceTagKeysAndTagValues: undefined,
      excludeResourceTagKeysAndTagValues: undefined,
    });
  };

  return (
    <>
      <Text size="md" weight="medium">
        Resource Details
      </Text>
      <FilterWrapper
        name="Date Added"
        isSelected={!!state?.dateAddedStart && !!state?.dateAddedEnd}
        selectedOptions={["dateAddedStart", "dateAddedEnd"]}
        selectedText={dateAddedSelectionText}
        onClear={clearDateAddedFilter}
      >
        <SelectableDateRange
          selectedRange={{
            startDate: state.dateAddedStart,
            endDate: state.dateAddedEnd,
          }}
          onRangeSelect={(range) =>
            updateState({
              dateAddedStart: range.startDate,
              dateAddedEnd: range.endDate,
            })
          }
          label="Time range when resource was added"
          setDefaultEndDateToToday
        />
      </FilterWrapper>
      <When condition={!provider}>
        <FilterWrapper
          name="Cloud Provider"
          isSelected={
            isNotNil(state.cloudProviders) && isNotEmpty(state.cloudProviders)
          }
          selectedOptions={state.cloudProviders ?? []}
          onClear={clearOperatingSystemsFilter}
        >
          <SelectableListWithSearch
            allowModeChange
            selectedOptions={state.cloudProviders ?? []}
            selectableItems={cloudProviderCheckboxes}
            isExcludeSelected={state.excludeCloudProviders}
            onMultiSelect={(cloudProviders) => updateState({ cloudProviders })}
            onModeChange={(mode) =>
              updateState({ excludeCloudProviders: mode === Mode.EXCLUDES })
            }
          />
        </FilterWrapper>
      </When>
      <FilterWrapper
        name="Operating System"
        isSelected={
          (isNotNil(state.operatingSystems) &&
            isNotEmpty(state.operatingSystems)) ||
          (isNotNil(state.operatingSystemSubTypes) &&
            isNotEmpty(state.operatingSystemSubTypes))
        }
        selectedOptions={state.operatingSystemSubTypes ?? []}
        selectedText={operatingSystemSelectionText}
        onClear={clearOperatingSystemFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.operatingSystems ?? []}
          selectedSubOptions={state.operatingSystemSubTypes ?? []}
          selectableItems={operatingSystemCheckboxes}
          isExcludeSelected={state.excludeOperatingSystems}
          onMultiSelect={(operatingSystems) => {
            updateState({ operatingSystems });
          }}
          onMultiSubSelect={(operatingSystemSubTypes) => {
            updateState({ operatingSystemSubTypes });
          }}
          onModeChange={(mode) =>
            updateState({ excludeOperatingSystems: mode === Mode.EXCLUDES })
          }
        />
      </FilterWrapper>
      <FilterWrapper
        name="Region"
        isSelected={isNotNil(state.regions) && isNotEmpty(state.regions)}
        selectedOptions={state.regions ?? []}
        onClear={clearRegionsFilter}
      >
        <SelectableListWithSearch
          isLoading={cloudProviderRegionsLoading}
          allowModeChange
          selectedOptions={state.regions ?? []}
          selectableItems={regionsCheckboxes ?? []}
          isExcludeSelected={state.excludeRegions}
          onMultiSelect={(regions) => updateState({ regions })}
          onModeChange={(mode) =>
            updateState({ excludeRegions: mode === Mode.EXCLUDES })
          }
        />
      </FilterWrapper>
      <FilterWrapper
        name="Resource Tag Key and Tag Value"
        isSelected={
          isNotNil(state.resourceTagKeysAndTagValues) &&
          isNotEmpty(state.resourceTagKeysAndTagValues)
        }
        selectedOptions={state.resourceTagKeysAndTagValues ?? []}
        selectedText={tagsSelectionText}
        onClear={clearResourceTagKeysAndTagValuesFilter}
      >
        <div style={{ padding: "8px" }}>
          <MultiTagsInput
            inputTags={state.resourceTagKeysAndTagValues ?? []}
            isExcludeSelected={state.excludeResourceTagKeysAndTagValues}
            onModeChange={(mode) => {
              updateState({
                excludeResourceTagKeysAndTagValues: mode === Mode.EXCLUDES,
              });
            }}
            onChange={(selections) => {
              updateState({ resourceTagKeysAndTagValues: selections });
            }}
            placeholderName={"Tag"}
          />
        </div>
      </FilterWrapper>
    </>
  );
}
